import { Injectable } from '@angular/core';

import { Am5Chart } from '../../../Common/Charts/models/am5-chart.model';
import { ChartDisplayElementModel } from '../models/chart-display-element.model';
import { ChartMetricsEnum } from '../enums/chart-metrics.enum';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { ReportBlockTypeEnum } from '../../../Common/Reports/enums/report-block-type.enum';
import { SocialSeriesMetricsEnum } from '../../../Social/Intelligence/enums/social-series-metrics.enum';


@Injectable()
export class Am5ChartExportLabelService {

    constructor(
        public localizationService: LocalizationService
    ) {}

    getExportLabels(type: ReportBlockTypeEnum, chart: Am5Chart, displayElement?: ChartDisplayElementModel): { [key: string]: string } {
        switch (type) {
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON:
            case ReportBlockTypeEnum.COVERAGE_IMPACT_TREND:
                return this.getCoverageOverTimeExportLabels(chart);
            case ReportBlockTypeEnum.SENTIMENT_TREND:
                return this.getSentimentTrendExportLabels(chart);
            case ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME:
            case ReportBlockTypeEnum.TOP_10_OUTLETS:
            case ReportBlockTypeEnum.TOP_HASHTAGS:
                return this.getTop10ChartsExportLabels(displayElement);
            case ReportBlockTypeEnum.TOP_SOCIAL_REFERRERS:
                return {
                    ...this.getTop10ChartsExportLabels(displayElement),
                    label: this.localizationService.transform('Reports.googleAnalyticsCharts.topSocialReferrals.label')
                };
            default:
                return {};
        }
    }

    private getCoverageOverTimeExportLabels(chart: Am5Chart): { [key: string]: string } {
        const dataFields: { [key: string]: string } = {
            label: this.localizationService.transform('Reports.label'),
            ['startTimeExport']: this.localizationService.transform('Reports.startTime'),
            ['endTimeExport']: this.localizationService.transform('Reports.endTime')
        };
        chart.series.forEach((x, i) => {
            dataFields[`value${i}`] = x.exportLabel;
        });
        // todo: export pr events, or annotations
        return dataFields;
    }

    private getSentimentTrendExportLabels(chart: Am5Chart): { [key: string]: string } {
        const dataFields: { [key: string]: string } = {
            label: this.localizationService.transform('Reports.label')
        };
        for (let i = chart.series.length - 1; i >= 0; i--) {
            dataFields[`value${i}`] = `${chart.series[i].label} (count)`;
        }
        for (let i = chart.series.length - 1; i >= 0; i--) {
            dataFields[`percent${i}`] = `${chart.series[i].label} (percent)`;
        }
        return dataFields;
    }

    private getTop10ChartsExportLabels(displayElement?: ChartDisplayElementModel): { [key: string]: string } {
        return {
            label: this.localizationService.transform('Reports.label'),
            value0: this.getLabelByMetric(displayElement?.series[0]?.metric ?? ChartMetricsEnum.volumeOfMentions, displayElement),
            percent0: this.localizationService.transform('Reports.percent')
        };
    }

    public getLabelByMetric(metric: ChartMetricsEnum, displayElement?: ChartDisplayElementModel): string {
        let label: string;

        switch (metric) {
            case ChartMetricsEnum.audienceReach:
            case ChartMetricsEnum.IMPRESSIONS:
                label = this.localizationService.transform('Reports.analysis.type.audienceReach');
                break;
            case ChartMetricsEnum.ave:
                const currency = displayElement?.styles.currency || 'USD';
                label = this.localizationService.transform('Reports.analysis.type.ave') + `(${currency})`;
                break;
            case ChartMetricsEnum.sessions:
            case ChartMetricsEnum.users:
                label = this.localizationService.transform('Reports.googleAnalyticsCharts.totalReferrals');
                break;
            case ChartMetricsEnum.ENGAGEMENTS:
                label = this.localizationService.transform('Reports.analysis.type.engagements');
                break;
            case ChartMetricsEnum.ENGAGEMENT_RATE:
                label = this.localizationService.transform(`Reports.analysis.type.${SocialSeriesMetricsEnum.ENGAGEMENT_RATE}`);
                break;
            case ChartMetricsEnum.LIKES:
            case ChartMetricsEnum.APPLAUSE:
                label = this.localizationService.transform(`Reports.analysis.type.${SocialSeriesMetricsEnum.APPLAUSE}`);
                break;
            case ChartMetricsEnum.CONVERSATION:
                label = this.localizationService.transform(`Reports.analysis.type.${SocialSeriesMetricsEnum.CONVERSATION}`);
                break;
            case ChartMetricsEnum.SHARES:
            case ChartMetricsEnum.AMPLIFICATION:
                label = this.localizationService.transform(`Reports.analysis.type.${SocialSeriesMetricsEnum.AMPLIFICATION}`);
                break;
            case ChartMetricsEnum.VIEWS:
                label = this.localizationService.transform(`Reports.analysis.type.${SocialSeriesMetricsEnum.VIEWS}`);
                break;
            case ChartMetricsEnum.volumeOfMentions:
            default:
                label = this.localizationService.transform('Reports.analysis.type.volumeOfMentions');
                break;
        }

        return label;
    }
}
